/**
 * MacroView - Multiple lives running simultaneously
 * 
 * Shows:
 * - Grid of micro-boards (10 lives)
 * - System-wide metrics
 * - Collective strain visualization
 * - Click any life to zoom in
 */
export default function MacroView({ lives, macroState, onZoomIn, queuedEvents }) {
  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Macro Header */}
      <div className="text-center mb-8">
        <p className="text-2xl text-gray-400 font-light tracking-wide mb-2">
          Multiple lives.
        </p>
        <p className="text-sm text-gray-600 font-mono">
          Click any life to zoom in
        </p>
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <MetricCard
          label="Average Stability"
          value={`${Math.floor(macroState.avgStability)}%`}
          color={macroState.avgStability > 70 ? 'green' : macroState.avgStability > 40 ? 'yellow' : 'red'}
        />
        <MetricCard
          label="System Strain"
          value={`${Math.floor(macroState.systemStrain * 100)}%`}
          color={macroState.systemStrain < 0.3 ? 'green' : macroState.systemStrain < 0.6 ? 'yellow' : 'red'}
        />
        <MetricCard
          label="Recovery Pool"
          value={`${Math.floor(macroState.sharedRecoveryPool * 100)}%`}
          color={macroState.sharedRecoveryPool > 0.7 ? 'green' : macroState.sharedRecoveryPool > 0.4 ? 'yellow' : 'red'}
        />
      </div>

      {/* Lives Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {lives.map((life, index) => (
          <LifePreviewCard
            key={life.id}
            life={life}
            index={index}
            onClick={() => onZoomIn(life.id)}
            hasIncomingEvent={queuedEvents.some(e => e.targetLife?.id === life.id)}
          />
        ))}
      </div>

      {/* Narrative Feedback */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
        <p className="text-gray-400 font-light text-lg">
          {getNarrativeFeedback(macroState)}
        </p>
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({ label, value, color }) {
  const colorClasses = {
    green: 'text-green-400 border-green-500/30',
    yellow: 'text-yellow-400 border-yellow-500/30',
    red: 'text-red-400 border-red-500/30'
  };

  return (
    <div className={`bg-white/5 rounded-xl p-4 border ${colorClasses[color]}`}>
      <div className={`text-3xl font-mono font-bold mb-1 ${colorClasses[color]}`}>
        {value}
      </div>
      <div className="text-sm text-gray-500 font-mono">{label}</div>
    </div>
  );
}

// Life Preview Card
function LifePreviewCard({ life, index, onClick, hasIncomingEvent }) {
  const avgCondition = Object.values(life.zones).reduce((sum, zone) => sum + zone.condition, 0) / 4;
  const conditionColor = avgCondition > 70 ? 'bg-green-500' : avgCondition > 40 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <button
      onClick={onClick}
      className="bg-white/5 hover:bg-white/10 rounded-lg p-4 border border-white/10 transition-all cursor-pointer group relative"
    >
      {/* Life number */}
      <div className="text-xs text-gray-600 font-mono mb-2">Life {index + 1}</div>

      {/* Mini zones visualization */}
      <div className="grid grid-cols-2 gap-1 mb-3">
        {Object.entries(life.zones).map(([key, zone]) => (
          <div key={key} className="h-8 bg-white/5 rounded overflow-hidden">
            <div
              className={`h-full transition-all ${getZoneColor(zone.condition)}`}
              style={{ width: `${zone.condition}%` }}
            />
          </div>
        ))}
      </div>

      {/* Stability */}
      <div className="text-lg font-mono font-bold">{Math.floor(life.stabilityMeter)}%</div>
      <div className="text-xs text-gray-500">Stability</div>

      {/* Warning indicator */}
      {hasIncomingEvent && (
        <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      )}

      {/* Hover effect */}
      <div className="absolute inset-0 border-2 border-blue-500/0 group-hover:border-blue-500/50 rounded-lg transition-all" />
    </button>
  );
}

function getZoneColor(condition) {
  if (condition > 70) return 'bg-green-500';
  if (condition > 40) return 'bg-yellow-500';
  return 'bg-red-500';
}

function getNarrativeFeedback(macroState) {
  if (macroState.systemStrain < 0.3) {
    return "The system is resilient. Each life contributes to collective stability.";
  } else if (macroState.systemStrain < 0.6) {
    return "Strain is building. When one life struggles, others feel the weight.";
  } else {
    return "The system is under pressure. Individual recovery slows as collective capacity diminishes.";
  }
}

